import { ConfirmationNumber, Person, Mic, CardGiftcard, Business, Directions, Event, CalendarMonth, LocationOn, Schedule } from '@mui/icons-material';
import { Paper, Box, Typography, Stack, Chip, Divider, Grid, Alert, Card, CardContent, Button, alpha } from '@mui/material';
import { format, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { formatDateRange } from '../../../utils/formatters';
import { RegistrationType } from '../../../enums/RegistrationType';
import { RegistrationStatus } from '../../../enums/RegistrationStatus';
import { ILocationList } from '../../../interfaces/ILocationList';
import LoadingAnimation from '../../LoadingScreen/LoadingAnimation';
import { useNotifications } from '@toolpad/core';
import { useApiClient } from '../../../ApiClientProvider';
import { ParticipantEventService } from '../../../services/participantEventService';
import { IUserRegistration } from '../../../interfaces/IUserRegistration';
import CurrencyDisplay from '../../CurrencyDisplay';
import { IPublicEventDetails } from '../../../interfaces/IPublicEventDetails';

interface IRegisteredUserSummaryProps {
    event: IPublicEventDetails;
    locations: ILocationList[];
    onViewMyTickets: () => void;
    onBuyMoreTickets: () => void;
    onAddToCalendar: () => void;
    onGetDirections: (locationId: string) => void;
}

const RegisteredUserSummary: React.FC<IRegisteredUserSummaryProps> = ({
    event,
    locations,
    onViewMyTickets,
    onBuyMoreTickets,
    onAddToCalendar,
    onGetDirections,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [registration, setRegistration] = useState<IUserRegistration>();
    const notifications = useNotifications();
    const apiClient = useApiClient('applicationClient');
    const eventService = new ParticipantEventService(apiClient);

    const getRegistrationTypeInfo = (type: RegistrationType) => {
        switch (type) {
            case RegistrationType.TicketPurchase:
                return {
                    label: 'Ticket Purchase',
                    icon: <ConfirmationNumber />,
                    color: 'primary' as const,
                    description: 'You purchased tickets for this event'
                };
            case RegistrationType.TicketAllocation:
                return {
                    label: 'Ticket Allocation',
                    icon: <Person />,
                    color: 'secondary' as const,
                    description: 'A ticket was allocated to you'
                };
            case RegistrationType.SessionDelegate:
                return {
                    label: 'Session Delegate',
                    icon: <Mic />,
                    color: 'success' as const,
                    description: 'You are a participant for this event'
                };
            case RegistrationType.Complimentary:
                return {
                    label: 'Complimentary',
                    icon: <CardGiftcard />,
                    color: 'info' as const,
                    description: 'You received complimentary access to this event'
                };
            case RegistrationType.Sponsor:
                return {
                    label: 'Sponsor',
                    icon: <Business />,
                    color: 'warning' as const,
                    description: 'You have sponsor access to this event'
                };
            default:
                return {
                    label: 'Registered',
                    icon: <Person />,
                    color: 'default' as const,
                    description: 'You are registered for this event'
                };
        }
    };

    const getStatusInfo = (status: RegistrationStatus) => {
        switch (status) {
            case RegistrationStatus.Registered:
                return { label: 'Confirmed', color: 'success' as const };
            case RegistrationStatus.CheckedIn:
                return { label: 'Checked In', color: 'primary' as const };
            case RegistrationStatus.Cancelled:
                return { label: 'Cancelled', color: 'error' as const };
            case RegistrationStatus.PendingTicketAllocation:
                return { label: 'No Ticket', color: 'warning' as const };
            default:
                return { label: status, color: 'default' as const };
        }
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return 'N/A';
        const isoString = dateString.endsWith('Z') ? dateString : `${dateString}Z`;
        return format(parseISO(isoString), 'MMM d, yyyy h:mm a');
    }

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const result = await eventService.getUserRegistration(event.id ?? '');
                setRegistration(result);
            } catch (error) {
                notifications.show("Error loading registraion data", { autoHideDuration: 3000 });
            } finally {
                setLoading(false);
            }
        }
        if (event?.id)
            fetchData();
    }, [event?.id]);

    if (loading) return <LoadingAnimation />;

    if (!registration) {
        return (
            <Paper elevation={3} sx={{ p: 4, borderRadius: 2, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        Registration Not Found
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        We could not find your registration for this event. If you believe this is an error, please contact support or try refreshing the page.
                    </Typography>
                </Alert>
            </Paper>
        );
    } else {
        const typeInfo = getRegistrationTypeInfo(registration.type);
        const statusInfo = getStatusInfo(registration.status);
        const sessionAssignments = registration.sessionAssignments ?? [];
        const isDelegateParticipant = registration.type === RegistrationType.SessionDelegate || sessionAssignments.length > 0;
        const summaryTitle = isDelegateParticipant ? "You're Participating in this Event" : "You're Registered!";
        const summaryDescription = isDelegateParticipant && registration.status === RegistrationStatus.PendingTicketAllocation
            ? 'You have delegate access for your assigned sessions. Ticket allocation may still be pending.'
            : typeInfo.description;

        return (
            <Paper 
                elevation={3} 
                sx={{ 
                    p: 4, 
                    borderRadius: 3,
                    mb: 4,
                    border: `1px solid ${alpha('#493193', 0.1)}`,
                    boxShadow: `0 8px 32px ${alpha('#493193', 0.08)}`,
                }}
            >
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Typography 
                            variant="h5" 
                            gutterBottom 
                            sx={{ 
                                fontWeight: 700,
                                background: `linear-gradient(135deg, #493193 0%, #2fadd8 100%)`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {summaryTitle}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            <Chip
                                icon={typeInfo.icon}
                                label={typeInfo.label}
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    height: { xs: 28, sm: 32 },
                                    fontWeight: 600,
                                    borderRadius: '50px',
                                    bgcolor: alpha('#493193', 0.1),
                                    color: '#493193',
                                    border: `1px solid ${alpha('#493193', 0.2)}`,
                                    '& .MuiChip-icon': {
                                        fontSize: { xs: 16, sm: 22 },
                                        color: '#493193',
                                    },
                                }}
                            />
                            <Chip
                                label={statusInfo.label}
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    height: { xs: 28, sm: 32 },
                                    fontWeight: 600,
                                    borderRadius: '50px',
                                    bgcolor: statusInfo.color === 'success' ? alpha('#2e7d32', 0.1) : alpha('#493193', 0.1),
                                    color: statusInfo.color === 'success' ? '#2e7d32' : '#493193',
                                    border: `1px solid ${statusInfo.color === 'success' ? alpha('#2e7d32', 0.2) : alpha('#493193', 0.2)}`,
                                }}
                            />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            {summaryDescription}
                        </Typography>
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Registration Details */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" color="text.secondary">Registration Date</Typography>
                        <Typography variant="body1">{formatDate(registration.registrationDate)}</Typography>
                    </Grid>

                    {registration.type === RegistrationType.TicketPurchase && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="subtitle2" color="text.secondary">Total Paid</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                <CurrencyDisplay value={registration.totalAmount} />
                            </Typography>
                        </Grid>
                    )}

                    {registration.allocatedBy && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="subtitle2" color="text.secondary">Allocated By</Typography>
                            <Typography variant="body1">Event Organizer</Typography>
                        </Grid>
                    )}

                    {isDelegateParticipant && sessionAssignments.length > 0 && (
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                Your Session Roles
                            </Typography>
                            <Stack spacing={1}>
                                {sessionAssignments.map((assignment) => (
                                    <Box
                                        key={`${assignment.sessionId}-${assignment.role}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            p: 1.25,
                                            borderRadius: 1.5,
                                            bgcolor: alpha('#493193', 0.04),
                                            border: `1px solid ${alpha('#493193', 0.12)}`,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                                            <Schedule sx={{ fontSize: 18, color: '#493193' }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                                {assignment.sessionTitle}
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            size="small"
                                            label={assignment.role}
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: alpha('#2fadd8', 0.12),
                                                color: '#2fadd8',
                                                border: `1px solid ${alpha('#2fadd8', 0.25)}`,
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </Grid>
                    )}
                </Grid>

                {registration.notes && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Additional Information
                        </Typography>
                        <Typography variant="body2">
                            {registration.notes}
                        </Typography>
                    </Alert>
                )}

                <Divider sx={{ mb: 3 }} />

                {/* Quick Actions */}
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Quick Actions
                    </Typography>

                    <Grid container spacing={2}>
                        {/* Event Actions */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: alpha('#493193', 0.4),
                                        boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                    <Event sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                        Add to Calendar
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={onAddToCalendar}
                                        fullWidth
                                        sx={{
                                            borderRadius: '50px',
                                            borderColor: alpha('#493193', 0.3),
                                            color: '#493193',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#493193',
                                                bgcolor: alpha('#493193', 0.05),
                                            },
                                        }}
                                    >
                                        Add Event
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Directions */}
                        {locations.length > 0 && (
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Card 
                                    variant="outlined" 
                                    sx={{ 
                                        height: '100%',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: alpha('#493193', 0.4),
                                            boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                        <Directions sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                            {locations.length === 1 ? 'Get Directions' : 'Plan Route'}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => onGetDirections(locations[0].id)}
                                            fullWidth
                                            sx={{
                                                borderRadius: '50px',
                                                borderColor: alpha('#493193', 0.3),
                                                color: '#493193',
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    borderColor: '#493193',
                                                    bgcolor: alpha('#493193', 0.05),
                                                },
                                            }}
                                        >
                                            {locations.length === 1 ? 'Directions' : 'Plan Route'}
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        )}

                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: alpha('#493193', 0.4),
                                        boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                    <ConfirmationNumber sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                        My Tickets
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={onViewMyTickets}
                                        fullWidth
                                        sx={{
                                            borderRadius: '50px',
                                            borderColor: alpha('#493193', 0.3),
                                            color: '#493193',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            '&:hover': {
                                                borderColor: '#493193',
                                                bgcolor: alpha('#493193', 0.05),
                                            },
                                        }}
                                    >
                                        View Tickets
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Buy More Tickets */}
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Card 
                                variant="outlined" 
                                sx={{ 
                                    height: '100%',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: alpha('#493193', 0.4),
                                        boxShadow: `0 4px 12px ${alpha('#493193', 0.15)}`,
                                        transform: 'translateY(-2px)',
                                    },
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                    <ConfirmationNumber sx={{ fontSize: 32, mb: 1, color: '#493193' }} />
                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                        Buy More Tickets
                                    </Typography>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        onClick={onBuyMoreTickets}
                                        fullWidth
                                        sx={{
                                            borderRadius: '50px',
                                            bgcolor: '#493193',
                                            color: 'white',
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                                            '&:hover': {
                                                bgcolor: '#3a2676',
                                                boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                                            },
                                        }}
                                    >
                                        Buy More
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Event Summary */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Event Summary
                    </Typography>
                    <Stack spacing={1}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarMonth fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                                {formatDateRange(event.startDate, event.endDate, event.isSingleDay)}
                            </Typography>
                        </Box>
                        {locations.length > 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <LocationOn fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {locations.length === 1 ? 'Location' : `${locations.length} Locations`}
                                    </Typography>
                                </Box>
                                <Stack spacing={1.5} sx={{ ml: 3 }}>
                                    {locations.map((location, index) => (
                                        <Box key={location.id} sx={{
                                            p: 1.5,
                                            bgcolor: 'background.paper',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'divider'
                                        }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                        {location.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        {location.streetAddress}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                        {location.city}, {location.region} {location.postalCode}
                                                    </Typography>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => onGetDirections(location.id)}
                                                    sx={{
                                                        ml: 1,
                                                        minWidth: 'auto',
                                                        px: 2,
                                                        borderRadius: '50px',
                                                        fontSize: '0.75rem',
                                                        borderColor: alpha('#493193', 0.3),
                                                        color: '#493193',
                                                        fontWeight: 600,
                                                        textTransform: 'none',
                                                        '&:hover': {
                                                            borderColor: '#493193',
                                                            bgcolor: alpha('#493193', 0.05),
                                                        },
                                                    }}
                                                >
                                                    Directions
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Paper>
        );
    }
};

export default RegisteredUserSummary;